import * as THREE from 'three';
import type { System } from '../../systems';
import type { InputManager } from '../../input';
import type { EntityRegistry } from '../../entities';
import type { PlayerShip } from '../ships';
import type { AudioManager } from '../../audio';
import { Asteroid } from '../sector/Asteroid';
import { NpcShip } from '../npcs/NpcShip';
import { ProjectilePool } from './ProjectilePool';
import type { Explosions, ShipExplosionPalette } from './Explosions';

export interface CombatConfig {
  readonly projectileSpeed: number;
  readonly fireInterval: number;
  readonly damage: number;
  readonly aimDistance: number;
}

export interface CombatHooks {
  readonly onAsteroidDestroyed?: (
    asteroid: Asteroid,
    worldPos: THREE.Vector3
  ) => void;
  readonly onNpcHit?: (npc: NpcShip, damage: number) => void;
  readonly onNpcDestroyed?: (npc: NpcShip, worldPos: THREE.Vector3) => void;
}

export const DEFAULT_COMBAT_CONFIG: CombatConfig = {
  projectileSpeed: 200,
  fireInterval: 0.11,
  damage: 1,
  aimDistance: 260
};

/** Bullet damage falloff against asteroids. The kinetic round chips rocks
 *  but is poor at it — 5x slower than the dedicated ship mining beam.
 *  Keeps NPC ship damage at full effectiveness so combat balance is
 *  unchanged; only the "use the right tool for the job" pressure increases. */
export const BULLET_VS_ASTEROID_DAMAGE_FACTOR = 0.2;

export class CombatSystem implements System {
  readonly id = 'combat';

  private readonly projectiles: ProjectilePool;
  private readonly muzzleA = new THREE.Vector3(-0.95, -0.05, -0.35);
  private readonly muzzleB = new THREE.Vector3(0.95, -0.05, -0.35);
  private readonly muzzleWorld = new THREE.Vector3();
  private readonly cameraForward = new THREE.Vector3();
  private readonly aimPoint = new THREE.Vector3();
  private readonly direction = new THREE.Vector3();
  private readonly velocity = new THREE.Vector3();
  private readonly hitPos = new THREE.Vector3();
  private barrel = 0;
  private fireCooldown = 0;
  private enabled = true;

  constructor(
    private readonly ship: PlayerShip,
    private readonly input: InputManager,
    private readonly entities: EntityRegistry,
    scene: THREE.Object3D,
    private readonly camera: THREE.Camera,
    private readonly explosions: Explosions,
    private readonly config: CombatConfig = DEFAULT_COMBAT_CONFIG,
    private readonly audio: AudioManager | null = null,
    private readonly hooks: CombatHooks = {}
  ) {
    this.projectiles = new ProjectilePool(scene, 48);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  triggerDestruction(worldPos: THREE.Vector3, size: number): void {
    this.explosions.spawnDestruction(worldPos, size);
  }

  triggerShipDestruction(
    worldPos: THREE.Vector3,
    size: number,
    palette: ShipExplosionPalette
  ): void {
    this.explosions.spawnShipDestruction(worldPos, size, palette);
  }

  update(dt: number): void {
    this.fireCooldown = Math.max(0, this.fireCooldown - dt);

    if (
      this.enabled &&
      this.input.isPointerLocked() &&
      this.input.isMouseDown(0) &&
      this.fireCooldown <= 0
    ) {
      this.fire();
      this.fireCooldown = this.config.fireInterval;
    }

    this.projectiles.update(dt);
    this.checkCollisions();
  }

  dispose(): void {
    this.projectiles.dispose();
  }

  private fire(): void {
    this.ship.object3D.updateMatrixWorld();
    this.camera.updateMatrixWorld();

    this.cameraForward
      .set(0, 0, -1)
      .applyQuaternion(this.camera.quaternion);

    this.aimPoint
      .copy(this.camera.position)
      .addScaledVector(this.cameraForward, this.config.aimDistance);

    const local = this.barrel % 2 === 0 ? this.muzzleA : this.muzzleB;
    this.barrel++;
    this.muzzleWorld.copy(local).applyMatrix4(this.ship.object3D.matrixWorld);

    this.direction
      .copy(this.aimPoint)
      .sub(this.muzzleWorld)
      .normalize();

    this.velocity
      .copy(this.direction)
      .multiplyScalar(this.config.projectileSpeed)
      .add(this.ship.velocity);

    this.projectiles.spawn(this.muzzleWorld, this.velocity, this.config.damage);
    this.audio?.playLaser();
  }

  private checkCollisions(): void {
    for (const projectile of this.projectiles.pool) {
      if (!projectile.active) continue;

      const prev = projectile.prevPos;
      const curr = projectile.object3D.position;
      const abx = curr.x - prev.x;
      const aby = curr.y - prev.y;
      const abz = curr.z - prev.z;
      const abLenSq = abx * abx + aby * aby + abz * abz;

      for (const entity of this.entities.all().values()) {
        const isAsteroid = entity instanceof Asteroid;
        const isNpc = entity instanceof NpcShip;
        if (!isAsteroid && !isNpc) continue;
        if (!entity.isAlive) continue;

        const damageable = entity as Asteroid | NpcShip;
        const aPos = damageable.object3D.position;
        const apx = aPos.x - prev.x;
        const apy = aPos.y - prev.y;
        const apz = aPos.z - prev.z;

        let t = 0;
        if (abLenSq > 1e-6) {
          t = (apx * abx + apy * aby + apz * abz) / abLenSq;
          if (t < 0) t = 0;
          else if (t > 1) t = 1;
        }
        const cx = prev.x + abx * t;
        const cy = prev.y + aby * t;
        const cz = prev.z + abz * t;
        const dx = aPos.x - cx;
        const dy = aPos.y - cy;
        const dz = aPos.z - cz;
        const sum = projectile.radius + damageable.collisionRadius;
        if (dx * dx + dy * dy + dz * dz >= sum * sum) continue;

        this.hitPos.set(cx, cy, cz);
        // Asteroids take a heavily reduced share of kinetic damage —
        // the player ship's mining beam is the proper tool. NPC ships
        // still take full damage so combat tuning stays untouched.
        const damageDealt = isAsteroid
          ? projectile.damage * BULLET_VS_ASTEROID_DAMAGE_FACTOR
          : projectile.damage;
        const destroyed = damageable.takeDamage(damageDealt);
        projectile.deactivate();

        if (destroyed) {
          if (isAsteroid) {
            const ast = damageable as Asteroid;
            this.explosions.spawnDestruction(aPos, ast.collisionRadius, ast.baseTint);
            ast.markForRemoval();
            this.audio?.playExplosion(ast.collisionRadius);
            this.hooks.onAsteroidDestroyed?.(ast, aPos);
          } else {
            const npc = damageable as NpcShip;
            this.explosions.spawnShipDestruction(
              aPos,
              npc.collisionRadius * 1.4,
              npc.skin
            );
            npc.markForRemoval();
            this.audio?.playExplosion(npc.collisionRadius * 1.4);
            this.hooks.onNpcDestroyed?.(npc, aPos);
          }
        } else {
          this.explosions.spawnHit(this.hitPos);
          this.audio?.playHit();
          if (isNpc) {
            this.hooks.onNpcHit?.(damageable as NpcShip, projectile.damage);
          }
        }
        break;
      }
    }
  }
}
